import { CaseTypeNamesProvider } from '@/hooks/useCaseTypeNames';
import { lazy, Suspense, useEffect } from 'react';
import { pageLoaders } from '@/pageLoaders';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ToastProvider } from '@/hooks/useToast';
import { AuthProvider } from '@/hooks/useAuth';
import { PermissionProvider } from '@/hooks/usePermission';
import { captureDocuSignCallback } from '@/utils/docusignOAuth';
import DocuSignCallbackPage from '@/pages/DocuSignCallbackPage';

// callback query は認証・画面描画が始まる前に除去する。
captureDocuSignCallback();

// レイアウト
import AppLayout from '@/components/layout/AppLayout';

// ローディング表示
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// RBAC ルートガード (F10)
import RequirePermission from '@/components/auth/RequirePermission';
import { AuthorizedHome } from '@/components/auth/AuthorizedHome';

// ページ (Lazy Loading)
const DashboardPage = lazy(pageLoaders.DashboardPage);
const CasePatrolPage = lazy(pageLoaders.CasePatrolPage);
const ClientsPage = lazy(pageLoaders.ClientsPage);
// steering 577: 社内タスク管理（β版）。steering 504 の「相談タスク」一覧を一般化した。
// **SPA の画面 route は `/client-tasks` のまま**(既存 bookmark を壊さない)、
// BE API path は `/tasks` で別契約。旧 `/clients/tasks` は依頼者詳細の節が使い続ける。
const ClientTasksPage = lazy(pageLoaders.ClientTasksPage);
const CasesPage = lazy(pageLoaders.CasesPage);
const HouseholdBudgetsPage = lazy(pageLoaders.HouseholdBudgetsPage);
// #406 銀行CSV取込・入金照合・預り金台帳 Slice 1: 入金明細取込プレビュー
const BankImportPreviewPage = lazy(pageLoaders.BankImportPreviewPage);
// #406 Slice 2: 取込履歴一覧 / batch 詳細
const BankImportHistoryPage = lazy(pageLoaders.BankImportHistoryPage);
const BankImportBatchDetailPage = lazy(pageLoaders.BankImportBatchDetailPage);
// #406 Slice 3: 入金照合キュー / 仮想口座管理 / 受入口座管理
const PaymentMatchQueuePage = lazy(pageLoaders.PaymentMatchQueuePage);
// steering 460 gap-C: 未入金者リスト
const DelinquencyPage = lazy(pageLoaders.DelinquencyPage);
const VirtualAccountsPage = lazy(pageLoaders.VirtualAccountsPage);
const ReceivingAccountsPage = lazy(pageLoaders.ReceivingAccountsPage);
const CollectionsPage = lazy(pageLoaders.CollectionsPage);
const VaultPage = lazy(pageLoaders.VaultPage);
const AdminPage = lazy(pageLoaders.AdminPage);
const ClientGroupsPage = lazy(pageLoaders.ClientGroupsPage);
const CalendarPage = lazy(pageLoaders.CalendarPage);
const LoginPage = lazy(pageLoaders.LoginPage);
const SsoCallbackPage = lazy(pageLoaders.SsoCallbackPage);
const ReportsPage = lazy(pageLoaders.ReportsPage);
const TemplatesPage = lazy(pageLoaders.TemplatesPage);
const DocMasterPage = lazy(pageLoaders.DocMasterPage);
// 債権者マスタ: 既存の CreditorMasterManager をモーダル外からも開ける専用ページ (導線の明確化)
const CreditorMasterPage = lazy(pageLoaders.CreditorMasterPage);
const MessagesPage = lazy(pageLoaders.MessagesPage);
// steering 444: 横長モニター向けメッセージ対応 workspace (/messages = メッセージ設定 とは別画面)
const MessageWorkspacePage = lazy(pageLoaders.MessageWorkspacePage);
// steering 463: 設定 > 組織設定 (6 タブ)。旧 SettingsPage は廃止し /settings は redirect のみ
const OrgSettingsPage = lazy(pageLoaders.OrgSettingsPage);
const PreferencesPage = lazy(pageLoaders.PreferencesPage);
const AiConnectionsPage = lazy(pageLoaders.AiConnectionsPage);
const BugReportPage = lazy(pageLoaders.BugReportPage);
const RequestStatusPage = lazy(pageLoaders.RequestStatusPage);
const HelpPage = lazy(pageLoaders.HelpPage);
const PortalPage = lazy(pageLoaders.PortalPage);
const PortalBudgetPage = lazy(pageLoaders.PortalBudgetPage);
// steering 474: SMS の短縮 URL (`/r/{code}`) の着地ページ。code を portal token へ解決して
// 既存 PortalPage へ置換遷移するだけで、**認証状態は一切作らない**。
const ShortLinkRedirectPage = lazy(pageLoaders.ShortLinkRedirectPage);
const PlatformPage = lazy(pageLoaders.PlatformPage);
const ChatworkPage = lazy(pageLoaders.ChatworkPage);
const LinePage = lazy(pageLoaders.LinePage);

// ==============================
// Surface mode 判定 (hostname ベース、ops v0.1)
// ==============================
// - ops:    ops.ace-collect.com / dev-ops.ace-collect.com — Platform Operator Console のみ
// - portal: hostname に 'portal' を含む — 依頼者ポータル (既存挙動維持)
// - app:    上記以外 (app.ace-collect.com / dev-app / localhost) — Tenant Console

export type SurfaceMode = 'ops' | 'portal' | 'app';

export function detectSurfaceMode(hostname: string): SurfaceMode {
  if (/^(dev-)?ops\./.test(hostname)) return 'ops';
  if (hostname.includes('portal')) return 'portal';
  return 'app';
}

const SURFACE_MODE = detectSurfaceMode(window.location.hostname);

/** 現在の app host から対応する ops origin を導出する (導出不能なら null = localhost 等) */
function deriveOpsOrigin(hostname: string): string | null {
  if (hostname.startsWith('dev-app.')) return `https://${hostname.replace('dev-app.', 'dev-ops.')}`;
  if (hostname.startsWith('app.')) return `https://${hostname.replace('app.', 'ops.')}`;
  return null;
}

/**
 * app mode の /platform アクセスを ops surface へ誘導する。
 * - app.ace-collect.com / dev-app.* → 対応する ops origin へ redirect
 * - localhost 等 (ops origin を導出できない開発環境) → 従来どおり superadmin guard 付きで表示
 */
function PlatformSurfaceRoute() {
  const opsOrigin = deriveOpsOrigin(window.location.hostname);

  useEffect(() => {
    if (opsOrigin) {
      window.location.replace(`${opsOrigin}/platform`);
    }
  }, [opsOrigin]);

  if (opsOrigin) {
    return <LoadingSpinner fullScreen />;
  }
  return (
    <RequirePermission role="superadmin">
      <Suspense fallback={<LoadingSpinner />}>
        <PlatformPage />
      </Suspense>
    </RequirePermission>
  );
}

/**
 * legacy `/settings` の redirect (steering 463)。
 *
 * 旧 3 タブ (外部連携 / マスタ管理 / 運用設定) の deep-link を、分解後の canonical URL へ
 * 振り分ける。**silent fallback にしない** — `App.tsx` の `*` fallback はダッシュボードへ
 * 飛ばすため、そのままだと利用者に「設定が消えた」と誤認させる。
 *
 * 着地先 (`/settings/org` / `/doc-master`) はいずれも redirect source ではない終端 route
 * なので、リダイレクトの閉路は生じない。
 */
export function LegacySettingsRedirect() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  // マスタ管理 3 subtab のうち書類マスタへ移した 2 件の先頭へ送る
  if (tab === 'masters') return <Navigate to="/doc-master?tab=categories" replace />;
  if (tab === 'operations') return <Navigate to="/settings/org?tab=operations" replace />;
  if (tab === 'integrations') return <Navigate to="/settings/org?tab=integrations" replace />;
  return <Navigate to="/settings/org" replace />;
}

/**
 * アプリケーションルートコンポーネント
 *
 * Provider スタック:
 *   BrowserRouter (main.tsx)
 *     → ToastProvider
 *       → AuthProvider
 *         → PermissionProvider
 *           → AppLayout → Routes
 */
export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <PermissionProvider>
          <CaseTypeNamesProvider>
            <Routes>
              {SURFACE_MODE === 'app' && (
                <Route path="/settings/docusign/callback" element={<DocuSignCallbackPage />} />
              )}
              {/* ポータル (顧客向け — 管理画面とは別の認証体系)。ops surface では登録しない */}
              {/* /portal/:token (管理画面ドメインからのアクセス) */}
              {SURFACE_MODE !== 'ops' && (
                <Route
                  path="/portal/:token"
                  element={
                    <Suspense fallback={<LoadingSpinner fullScreen />}>
                      <PortalPage />
                    </Suspense>
                  }
                />
              )}
              {/* /r/:code — SMS 短縮 URL の着地 (steering 474)。
              **`/:token` より前に置く**: `/:token` は単一 segment、こちらは 2 segment なので
              react-router の ranking 上は衝突しないが、「予約 segment が先」という意図を
              並び順でも明示する。portal surface 限定 (admin ドメインには生やさない)。 */}
              {SURFACE_MODE === 'portal' && (
                <Route
                  path="/r/:code"
                  element={
                    <Suspense fallback={<LoadingSpinner fullScreen />}>
                      <ShortLinkRedirectPage />
                    </Suspense>
                  }
                />
              )}

              {/* /:token (ポータル専用ドメイン dev-portal.ace-collect.com からのアクセス) */}
              {SURFACE_MODE === 'portal' && (
                <Route
                  path="/:token"
                  element={
                    <Suspense fallback={<LoadingSpinner fullScreen />}>
                      <PortalPage />
                    </Suspense>
                  }
                />
              )}

              {/* 家計表入力ポータル (steering 290)。collection portal と同じ token+password 認証。 */}
              {/* /portal/budget/:token (管理画面ドメインからのアクセス) */}
              {SURFACE_MODE !== 'ops' && (
                <Route
                  path="/portal/budget/:token"
                  element={
                    <Suspense fallback={<LoadingSpinner fullScreen />}>
                      <PortalBudgetPage />
                    </Suspense>
                  }
                />
              )}
              {/* /budget/:token (ポータル専用ドメインからのアクセス) */}
              {SURFACE_MODE === 'portal' && (
                <Route
                  path="/budget/:token"
                  element={
                    <Suspense fallback={<LoadingSpinner fullScreen />}>
                      <PortalBudgetPage />
                    </Suspense>
                  }
                />
              )}

              {/* ログインページ (レイアウトなし) */}
              <Route
                path="/login"
                element={
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <LoginPage />
                  </Suspense>
                }
              />

              {/* SSO コールバック (レイアウトなし) */}
              <Route
                path="/sso-callback"
                element={
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SsoCallbackPage />
                  </Suspense>
                }
              />

              {/* ops surface: Platform Operator Console のみ (tenant 画面 route は登録しない) */}
              {SURFACE_MODE === 'ops' && (
                <Route element={<AppLayout />}>
                  <Route index element={<Navigate to="/platform" replace />} />
                  <Route
                    path="platform/*"
                    element={
                      <RequirePermission role="superadmin">
                        <Suspense fallback={<LoadingSpinner />}>
                          <PlatformPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                </Route>
              )}

              {/* メインアプリケーション (AppLayout 内、app / portal surface) */}
              {SURFACE_MODE !== 'ops' && (
                <Route element={<AppLayout />}>
                  <Route
                    index
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AuthorizedHome>
                          <DashboardPage />
                        </AuthorizedHome>
                      </Suspense>
                    }
                  />
                  <Route
                    path="case-patrol"
                    element={
                      <RequirePermission area="dashboard">
                        <RequirePermission area="collections">
                          <Suspense fallback={<LoadingSpinner />}>
                            <CasePatrolPage />
                          </Suspense>
                        </RequirePermission>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="clients/*"
                    element={
                      <RequirePermission area="clients">
                        <Suspense fallback={<LoadingSpinner />}>
                          <ClientsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/*
                  steering 577: タスク管理（β版）。**権限は専用 area `tasks`**。
                  `clients` のままだと member / viewer (= `clients: none()`) が
                  自分のタスクすら開けない。route 文字列は変えない。
                */}
                  <Route
                    path="client-tasks"
                    element={
                      <RequirePermission area="tasks">
                        <Suspense fallback={<LoadingSpinner />}>
                          <ClientTasksPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="cases/*"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <CasesPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* 家計表管理 専用ページ (案件横断一覧、#293) */}
                  <Route
                    path="household-budgets/*"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <HouseholdBudgetsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* #406 入金明細取込プレビュー (組織横断、payments:execute で gate) */}
                  <Route
                    path="payments/imports"
                    element={
                      <RequirePermission area="payments" action="execute">
                        <Suspense fallback={<LoadingSpinner />}>
                          <BankImportPreviewPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* #406 Slice 2 取込履歴一覧 (payments:read で gate) */}
                  <Route
                    path="payments/imports/history"
                    element={
                      <RequirePermission area="payments" action="read">
                        <Suspense fallback={<LoadingSpinner />}>
                          <BankImportHistoryPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* #406 Slice 2 batch 詳細 + 明細 (payments:read で gate) */}
                  <Route
                    path="payments/imports/history/:batchId"
                    element={
                      <RequirePermission area="payments" action="read">
                        <Suspense fallback={<LoadingSpinner />}>
                          <BankImportBatchDetailPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* #406 Slice 3 入金照合キュー (payments:read で gate) */}
                  <Route
                    path="payments/matches"
                    element={
                      <RequirePermission area="payments" action="read">
                        <Suspense fallback={<LoadingSpinner />}>
                          <PaymentMatchQueuePage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* steering 460 gap-C 未入金者リスト (payments:read で gate) */}
                  <Route
                    path="payments/delinquency"
                    element={
                      <RequirePermission area="payments" action="read">
                        <Suspense fallback={<LoadingSpinner />}>
                          <DelinquencyPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* #406 Slice 3 仮想口座管理 (payments:read で gate。登録・無効化・付け替えは write) */}
                  <Route
                    path="payments/virtual-accounts"
                    element={
                      <RequirePermission area="payments" action="read">
                        <Suspense fallback={<LoadingSpinner />}>
                          <VirtualAccountsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* #406 Slice 3 受入口座管理 (payments:write で gate) */}
                  <Route
                    path="payments/receiving-accounts"
                    element={
                      <RequirePermission area="payments" action="write">
                        <Suspense fallback={<LoadingSpinner />}>
                          <ReceivingAccountsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="collections/*"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <CollectionsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="vault/*"
                    element={
                      <RequirePermission area="vault">
                        <Suspense fallback={<LoadingSpinner />}>
                          <VaultPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="calendar"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <CalendarPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="client-groups"
                    element={
                      <RequirePermission area="clientGroups">
                        <Suspense fallback={<LoadingSpinner />}>
                          <ClientGroupsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="reports"
                    element={
                      <RequirePermission area="reports">
                        <Suspense fallback={<LoadingSpinner />}>
                          <ReportsPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="templates"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <TemplatesPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="doc-master"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <DocMasterPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="creditor-master"
                    element={
                      <RequirePermission area="collections" action="write">
                        <Suspense fallback={<LoadingSpinner />}>
                          <CreditorMasterPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="messages"
                    element={
                      // ADR-101: メッセージ設定は中身の API と同じ orgSettings で判定する (閲覧 = read、変更 = write)
                      <RequirePermission area="orgSettings">
                        <Suspense fallback={<LoadingSpinner />}>
                          <MessagesPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* steering 444: メッセージ対応 workspace (3 ペイン)。
                    `/messages` (メッセージ設定) とは別 route / 別 surface。 */}
                  <Route
                    path="message-workspace"
                    element={
                      <RequirePermission area="collections">
                        <Suspense fallback={<LoadingSpinner />}>
                          <MessageWorkspacePage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* 個人の AI 連携の入口は全職員に表示する。
                    組織設定タブと他サービスは OrgSettingsPage 内で権限判定する。 */}
                  <Route
                    path="settings/org"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <OrgSettingsPage />
                      </Suspense>
                    }
                  />
                  {/* legacy `/settings` は silent fallback (未定義 URL → ダッシュボード) に
                    委ねず、明示的に replace redirect する */}
                  <Route path="settings" element={<LegacySettingsRedirect />} />
                  <Route
                    path="ai-connections"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AiConnectionsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="preferences"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PreferencesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="bug-report"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <BugReportPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="request-status"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <RequestStatusPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="help"
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <HelpPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="admin/*"
                    element={
                      <RequirePermission anyArea={['orgSettings', 'userMgmt', 'rbacSettings']}>
                        <Suspense fallback={<LoadingSpinner />}>
                          <AdminPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  {/* app surface の /platform は ops surface へ誘導する (ops v0.1)。
                  localhost 等 ops origin を導出できない環境のみ従来どおり表示 */}
                  <Route path="platform/*" element={<PlatformSurfaceRoute />} />
                  <Route
                    path="chatwork"
                    element={
                      <RequirePermission area="chatwork">
                        <Suspense fallback={<LoadingSpinner />}>
                          <ChatworkPage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="line"
                    element={
                      <RequirePermission area="line">
                        <Suspense fallback={<LoadingSpinner />}>
                          <LinePage />
                        </Suspense>
                      </RequirePermission>
                    }
                  />
                </Route>
              )}

              {/* フォールバック */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CaseTypeNamesProvider>
        </PermissionProvider>
      </AuthProvider>
    </ToastProvider>
  );
}
